import React, { useState, useRef } from 'react';
import {
  X,
  Flame,
  Clock,
  Star,
  MapPin,
  Calendar,
  Coffee,
  Wine,
  Trash2,
  ChevronDown,
  Check,
  Camera,
  Image,
  Loader2,
} from 'lucide-react';
import { Cigar } from '../data/cigars';
import {
  SmokeLogEntry,
  addSmokeLogEntry,
} from '../data/personalization';
import { saveBandImageLocal } from '../data/bands';

interface LogSmokeModalProps {
  cigar?: Cigar;
  onClose: () => void;
  onSave: (entry: SmokeLogEntry) => void;
}

export const LogSmokeModal: React.FC<LogSmokeModalProps> = ({
  cigar,
  onClose,
  onSave,
}) => {
  const [brand, setBrand] = useState(cigar?.brand || '');
  const [line, setLine] = useState(cigar?.line || '');
  const [rating, setRating] = useState<number>(0);
  const [duration, setDuration] = useState(cigar?.timeCommitment?.toString() || '');
  const [mood, setMood] = useState('');
  const [notes, setNotes] = useState('');
  const [location, setLocation] = useState('');
  const [pairing, setPairing] = useState('');
  const [wouldRepeat, setWouldRepeat] = useState(true);
  const [smokeDate, setSmokeDate] = useState(new Date().toISOString().split('T')[0]);

  // Band photo state
  const [bandImage, setBandImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const moods = ['Relaxed', 'Celebratory', 'Contemplative', 'Social', 'Working'];
  const pairings = ['Coffee', 'Whiskey', 'Bourbon', 'Rum', 'Wine', 'Beer', 'Cognac', 'Water', 'None'];

  // Handle band photo capture/selection
  const handleBandPhotoCapture = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be under 5MB');
      return;
    }

    setIsUploading(true);

    try {
      // Read file as data URL for preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        setBandImage(dataUrl);
        setIsUploading(false);
      };
      reader.onerror = () => {
        alert('Failed to read image');
        setIsUploading(false);
      };
      reader.readAsDataURL(file);
    } catch {
      alert('Failed to process image');
      setIsUploading(false);
    }
  };

  // Remove band photo
  const handleRemoveBandPhoto = () => {
    setBandImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!brand.trim()) return;

    let bandImageUrl: string | undefined;
    let bandImageId: string | undefined;

    // Upload band image if present
    if (bandImage) {
      setIsUploading(true);
      try {
        // Try to upload to server first
        const response = await fetch('/api/photos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dataUrl: bandImage, type: 'band' }),
        });

        if (response.ok) {
          const data = await response.json() as { data?: { url?: string; key?: string } };
          bandImageUrl = data.data?.url;
          bandImageId = data.data?.key;
        } else {
          // Fall back to local storage for guest mode
          bandImageId = saveBandImageLocal('temp', bandImage);
          bandImageUrl = bandImage; // Use data URL directly
        }
      } catch {
        // Fall back to local storage
        bandImageId = saveBandImageLocal('temp', bandImage);
        bandImageUrl = bandImage;
      }
      setIsUploading(false);
    }

    const entry = addSmokeLogEntry({
      cigarId: cigar?.id,
      brand: brand.trim(),
      line: line.trim() || undefined,
      smokeDate,
      duration: duration ? parseInt(duration) : undefined,
      rating: rating || undefined,
      mood: mood || undefined,
      notes: notes.trim() || undefined,
      location: location.trim() || undefined,
      pairing: pairing || undefined,
      wouldRepeat,
      bandImageUrl,
      bandImageId,
    });

    onSave(entry);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-4">
      <div className="bg-stone-900 rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden border border-stone-700">
        {/* Header */}
        <div className="sticky top-0 bg-stone-900 border-b border-stone-800 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Flame className="w-5 h-5 text-amber-500" />
            </div>
            <h2 className="text-lg font-semibold">Log Smoke</h2>
          </div>
          <button onClick={onClose} className="p-1 text-stone-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <div className="overflow-y-auto max-h-[70vh] p-4 space-y-4">
          {/* Cigar Info */}
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Brand *"
              value={brand}
              onChange={e => setBrand(e.target.value)}
              className="w-full px-4 py-3 bg-stone-800 border border-stone-700 rounded-lg text-white placeholder-stone-500 focus:outline-none focus:border-amber-500"
            />
            <input
              type="text"
              placeholder="Line / Vitola"
              value={line}
              onChange={e => setLine(e.target.value)}
              className="w-full px-4 py-3 bg-stone-800 border border-stone-700 rounded-lg text-white placeholder-stone-500 focus:outline-none focus:border-amber-500"
            />
          </div>

          {/* Band Photo Capture */}
          <div>
            <label className="block text-sm text-stone-400 mb-2">
              <Image className="w-4 h-4 inline mr-2" />
              Band Photo
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleBandPhotoCapture}
              className="hidden"
              id="band-photo-input"
            />
            {bandImage ? (
              <div className="relative">
                <img
                  src={bandImage}
                  alt="Band photo"
                  className="w-full h-32 object-cover rounded-lg border border-stone-700"
                />
                <button
                  onClick={handleRemoveBandPhoto}
                  className="absolute top-2 right-2 p-1.5 bg-red-500/80 hover:bg-red-500 rounded-full text-white"
                  type="button"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="w-full py-6 border-2 border-dashed border-stone-700 rounded-lg text-stone-400 hover:text-stone-300 hover:border-stone-600 transition-colors flex flex-col items-center gap-2"
                type="button"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-8 h-8 animate-spin" />
                    <span className="text-sm">Processing...</span>
                  </>
                ) : (
                  <>
                    <Camera className="w-8 h-8" />
                    <span className="text-sm">Tap to capture band</span>
                    <span className="text-xs text-stone-500">Add to your collection</span>
                  </>
                )}
              </button>
            )}
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm text-stone-400 mb-2">
              <Calendar className="w-4 h-4 inline mr-2" />
              Date
            </label>
            <input
              type="date"
              value={smokeDate}
              onChange={e => setSmokeDate(e.target.value)}
              className="w-full px-4 py-3 bg-stone-800 border border-stone-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
            />
          </div>

          {/* Rating */}
          <div>
            <label className="block text-sm text-stone-400 mb-2">
              <Star className="w-4 h-4 inline mr-2" />
              Rating
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className={`p-2 rounded-lg transition-colors ${
                    rating >= star
                      ? 'bg-amber-500 text-white'
                      : 'bg-stone-800 text-stone-500 hover:bg-stone-700'
                  }`}
                >
                  <Star className={`w-6 h-6 ${rating >= star ? 'fill-current' : ''}`} />
                </button>
              ))}
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm text-stone-400 mb-2">
              <Clock className="w-4 h-4 inline mr-2" />
              Duration (minutes)
            </label>
            <input
              type="number"
              placeholder="60"
              value={duration}
              onChange={e => setDuration(e.target.value)}
              className="w-full px-4 py-3 bg-stone-800 border border-stone-700 rounded-lg text-white placeholder-stone-500 focus:outline-none focus:border-amber-500"
            />
          </div>

          {/* Mood */}
          <div>
            <label className="block text-sm text-stone-400 mb-2">Mood</label>
            <div className="flex flex-wrap gap-2">
              {moods.map(m => (
                <button
                  key={m}
                  onClick={() => setMood(mood === m ? '' : m)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    mood === m
                      ? 'bg-amber-500 text-white'
                      : 'bg-stone-800 text-stone-300 hover:bg-stone-700'
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          {/* Pairing */}
          <div>
            <label className="block text-sm text-stone-400 mb-2">
              <Wine className="w-4 h-4 inline mr-2" />
              Pairing
            </label>
            <div className="flex flex-wrap gap-2">
              {pairings.map(p => (
                <button
                  key={p}
                  onClick={() => setPairing(pairing === p ? '' : p)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    pairing === p
                      ? 'bg-purple-500 text-white'
                      : 'bg-stone-800 text-stone-300 hover:bg-stone-700'
                  }`}
                >
                  {p === 'Coffee' ? <Coffee className="w-3 h-3 inline mr-1" /> : null}
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm text-stone-400 mb-2">
              <MapPin className="w-4 h-4 inline mr-2" />
              Location
            </label>
            <input
              type="text"
              placeholder="Home, lounge, patio..."
              value={location}
              onChange={e => setLocation(e.target.value)}
              className="w-full px-4 py-3 bg-stone-800 border border-stone-700 rounded-lg text-white placeholder-stone-500 focus:outline-none focus:border-amber-500"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm text-stone-400 mb-2">Notes</label>
            <textarea
              placeholder="How was the smoke?"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 bg-stone-800 border border-stone-700 rounded-lg text-white placeholder-stone-500 focus:outline-none focus:border-amber-500 resize-none"
            />
          </div>

          {/* Would Repeat */}
          <button
            onClick={() => setWouldRepeat(!wouldRepeat)}
            className={`w-full p-4 rounded-lg border transition-colors flex items-center justify-between ${
              wouldRepeat
                ? 'bg-green-500/10 border-green-500/30 text-green-400'
                : 'bg-stone-800 border-stone-700 text-stone-400'
            }`}
          >
            <span>Would smoke again</span>
            <div
              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                wouldRepeat ? 'border-green-500 bg-green-500' : 'border-stone-600'
              }`}
            >
              {wouldRepeat && <Check className="w-4 h-4 text-white" />}
            </div>
          </button>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-stone-900 border-t border-stone-800 p-4">
          <button
            onClick={handleSubmit}
            disabled={!brand.trim()}
            className="w-full py-3 bg-amber-600 hover:bg-amber-500 disabled:bg-stone-700 disabled:text-stone-500 rounded-lg font-medium transition-colors"
          >
            Log This Smoke
          </button>
        </div>
      </div>
    </div>
  );
};

// Smoke Log History View
interface SmokeLogViewProps {
  entries: SmokeLogEntry[];
  onDelete: (entryId: string) => void;
  onClose: () => void;
}

export const SmokeLogView: React.FC<SmokeLogViewProps> = ({
  entries,
  onDelete,
  onClose,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-4">
      <div className="bg-stone-900 rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[85vh] overflow-hidden border border-stone-700">
        {/* Header */}
        <div className="sticky top-0 bg-stone-900 border-b border-stone-800 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Flame className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Smoke Log</h2>
              <p className="text-xs text-stone-400">{entries.length} entries</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-stone-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Entries */}
        <div className="overflow-y-auto max-h-[70vh] p-4">
          {entries.length === 0 ? (
            <div className="text-center py-12">
              <Flame className="w-12 h-12 mx-auto text-stone-600 mb-4" />
              <p className="text-stone-400">No smokes logged yet</p>
              <p className="text-sm text-stone-500 mt-1">
                Log your first smoke to start tracking
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {entries.map(entry => (
                <div
                  key={entry.id}
                  className="bg-stone-800 rounded-xl border border-stone-700 overflow-hidden"
                >
                  <button
                    onClick={() =>
                      setExpandedId(expandedId === entry.id ? null : entry.id)
                    }
                    className="w-full p-4 text-left flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      {/* Band thumbnail */}
                      {entry.bandImageUrl && (
                        <img
                          src={entry.bandImageUrl}
                          alt=""
                          className="w-12 h-12 object-cover rounded-lg border border-stone-600 flex-shrink-0"
                        />
                      )}
                      <div>
                        <h3 className="font-medium">{entry.brand}</h3>
                        {entry.line && (
                          <p className="text-sm text-stone-400">{entry.line}</p>
                        )}
                        <div className="flex items-center gap-3 mt-1 text-xs text-stone-500">
                          <span>{formatDate(entry.smokeDate)}</span>
                          {entry.rating && (
                            <span className="flex items-center gap-1">
                              <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                              {entry.rating}/5
                            </span>
                          )}
                          {entry.duration && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {entry.duration}m
                            </span>
                          )}
                          {entry.bandImageUrl && (
                            <span className="flex items-center gap-1">
                              <Image className="w-3 h-3 text-amber-500" />
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <ChevronDown
                      className={`w-5 h-5 text-stone-500 transition-transform ${
                        expandedId === entry.id ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {expandedId === entry.id && (
                    <div className="px-4 pb-4 border-t border-stone-700 pt-3 space-y-2">
                      {/* Band Image */}
                      {entry.bandImageUrl && (
                        <div className="mb-3">
                          <img
                            src={entry.bandImageUrl}
                            alt={`${entry.brand} band`}
                            className="w-full h-24 object-cover rounded-lg border border-stone-700"
                          />
                        </div>
                      )}
                      {entry.mood && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-stone-400">Mood</span>
                          <span className="text-stone-300">{entry.mood}</span>
                        </div>
                      )}
                      {entry.pairing && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-stone-400">Pairing</span>
                          <span className="text-stone-300">{entry.pairing}</span>
                        </div>
                      )}
                      {entry.location && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-stone-400">Location</span>
                          <span className="text-stone-300">{entry.location}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-stone-400">Would repeat</span>
                        <span
                          className={
                            entry.wouldRepeat ? 'text-green-400' : 'text-red-400'
                          }
                        >
                          {entry.wouldRepeat ? 'Yes' : 'No'}
                        </span>
                      </div>
                      {entry.notes && (
                        <div className="mt-2 pt-2 border-t border-stone-700">
                          <p className="text-sm text-stone-300">{entry.notes}</p>
                        </div>
                      )}

                      <button
                        onClick={() => onDelete(entry.id)}
                        className="w-full mt-3 py-2 text-red-400 hover:text-red-300 flex items-center justify-center gap-2 text-sm"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Entry
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Quick Stats Component for Dashboard
interface SmokeLogStatsProps {
  entries: SmokeLogEntry[];
}

export const SmokeLogStats: React.FC<SmokeLogStatsProps> = ({ entries }) => {
  // Calculate stats
  const thisMonth = entries.filter(e => {
    const entryDate = new Date(e.smokeDate);
    const now = new Date();
    return (
      entryDate.getMonth() === now.getMonth() &&
      entryDate.getFullYear() === now.getFullYear()
    );
  });

  const avgRating =
    entries.filter(e => e.rating).reduce((sum, e) => sum + (e.rating || 0), 0) /
      entries.filter(e => e.rating).length || 0;

  const totalDuration = entries
    .filter(e => e.duration)
    .reduce((sum, e) => sum + (e.duration || 0), 0);

  return (
    <div className="grid grid-cols-3 gap-3">
      <div className="bg-stone-800/50 rounded-lg p-3 text-center">
        <div className="text-xl font-bold text-amber-500">{thisMonth.length}</div>
        <div className="text-xs text-stone-400">This Month</div>
      </div>
      <div className="bg-stone-800/50 rounded-lg p-3 text-center">
        <div className="text-xl font-bold text-amber-500">
          {avgRating ? avgRating.toFixed(1) : '-'}
        </div>
        <div className="text-xs text-stone-400">Avg Rating</div>
      </div>
      <div className="bg-stone-800/50 rounded-lg p-3 text-center">
        <div className="text-xl font-bold text-amber-500">
          {totalDuration > 0 ? `${Math.round(totalDuration / 60)}h` : '-'}
        </div>
        <div className="text-xs text-stone-400">Total Time</div>
      </div>
    </div>
  );
};
